import { useEffect } from 'react';

import type { RefObject } from 'react';

/**
 * The part of a DataGridPro api ref this hook reads. `virtualScrollerRef` is not on the public
 * `GridApiPro` type, hence optional; `rootElementRef` is the documented fallback.
 */
type ApiRef = RefObject<{
  virtualScrollerRef?: RefObject<HTMLElement | null>;
  rootElementRef?: RefObject<HTMLElement | null>;
} | null>;

function getScrollerFromApi(api: ApiRef): HTMLElement | null {
  const viaRef = api.current?.virtualScrollerRef?.current;
  if (viaRef) return viaRef;
  const root = api.current?.rootElementRef?.current;
  return root
    ? root.querySelector<HTMLElement>('.MuiDataGrid-virtualScroller')
    : null;
}

/**
 * Keeps the two grids scrolled together horizontally, by ratio.
 *
 * `resyncKey` re-subscribes the scroll listeners when it changes. `RosterPlan` passes the number of
 * displayed days, as a proxy for "DataGridPro replaced the virtual scroller nodes this effect
 * listens on". It is a proxy, not an equivalence: two 31-day months, or any two weeks, have the same
 * length, so a period change that keeps the column count does not re-subscribe. That is safe only as
 * long as DataGridPro keeps the scroller nodes across such a change; if scroll syncing is ever seen to
 * stop after switching period, this is the first place to look.
 */
export default function useSyncHorizontalScroll(
  apiTop: ApiRef,
  apiBottom: ApiRef,
  resyncKey?: unknown
) {
  useEffect(() => {
    const top = getScrollerFromApi(apiTop);
    const bottom = getScrollerFromApi(apiBottom);
    if (!top || !bottom) return;

    // Avoid browser smooth-scroll delays on follower
    top.style.scrollBehavior = 'auto';
    bottom.style.scrollBehavior = 'auto';

    const maxLeft = (el: HTMLElement) =>
      Math.max(0, el.scrollWidth - el.clientWidth);

    let programmatic = false;
    let rafId = 0;
    let lastLeftTop = top.scrollLeft;
    let lastLeftBottom = bottom.scrollLeft;
    let lastWheelTop = 0;
    let lastWheelBottom = 0;

    const markWheelTop = () => {
      lastWheelTop = performance.now();
    };
    const markWheelBottom = () => {
      lastWheelBottom = performance.now();
    };
    top.addEventListener('wheel', markWheelTop, { passive: true });
    bottom.addEventListener('wheel', markWheelBottom, { passive: true });

    const writeFollower = (
      src: HTMLElement,
      dst: HTMLElement,
      immediate: boolean
    ) => {
      const srcMax = maxLeft(src);
      const dstMax = maxLeft(dst);
      const pct = srcMax > 0 ? src.scrollLeft / srcMax : 0;
      const target = Math.round(pct * dstMax); // integer px to avoid micro-nudges

      if (programmatic) return;
      const doWrite = () => {
        programmatic = true;
        if (Math.abs(dst.scrollLeft - target) > 1) dst.scrollLeft = target;
        // release on next frame so follower's own 'scroll' doesn't bounce back
        requestAnimationFrame(() => {
          programmatic = false;
        });
      };

      if (immediate) {
        doWrite(); // wheel/trackpad → immediate feels snappier
      } else {
        if (rafId) cancelAnimationFrame(rafId); // thumb drag → coalesce to RAF
        rafId = requestAnimationFrame(doWrite);
      }
    };

    const onTopScroll = () => {
      const cur = top.scrollLeft;
      if (Math.abs(cur - lastLeftTop) <= 0.5) return; // vertical-only scroll
      lastLeftTop = cur;
      const immediate = performance.now() - lastWheelTop < 80;
      writeFollower(top, bottom, immediate);
    };

    const onBottomScroll = () => {
      const cur = bottom.scrollLeft;
      if (Math.abs(cur - lastLeftBottom) <= 0.5) return;
      lastLeftBottom = cur;
      const immediate = performance.now() - lastWheelBottom < 80;
      writeFollower(bottom, top, immediate);
    };

    top.addEventListener('scroll', onTopScroll, { passive: true });
    bottom.addEventListener('scroll', onBottomScroll, { passive: true });

    // Initial align by ratio
    writeFollower(top, bottom, true);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      top.removeEventListener('wheel', markWheelTop);
      bottom.removeEventListener('wheel', markWheelBottom);
      top.removeEventListener('scroll', onTopScroll);
      bottom.removeEventListener('scroll', onBottomScroll);
    };
    // `apiTop` and `apiBottom` come from `useGridApiRef()`, whose identity is stable for the
    // component's lifetime, so listing them never re-runs the effect on its own.
  }, [apiTop, apiBottom, resyncKey]);
}
